import React, { useState, useEffect } from 'react';

function TeamAnalysis({ playerData, teams }) {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teamStats, setTeamStats] = useState(null);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    if (selectedTeam && playerData.length > 0) {
      const teamPlayers = playerData.filter(player => player.Team === selectedTeam);
      const rounds = [...new Set(teamPlayers.map(p => p.Round))];

      const parseScore = (gb) => {
        if (!gb) return 0;
        const [goals, behinds] = gb.toString().split('.').map(Number);
        return (isNaN(goals) ? 0 : goals * 6) + (isNaN(behinds) ? 0 : behinds);
      };
      
      const matchResults = rounds.map(round => {
        const roundPlayers = playerData.filter(p => p.Round === round);
        const oppositionTeam = [...new Set(roundPlayers.map(p => p.Team))]
          .find(team => team !== selectedTeam);
  
        const teamRoundStats = roundPlayers.filter(p => p.Team === selectedTeam);
        const oppositionRoundStats = roundPlayers.filter(p => p.Team === oppositionTeam);
  
        const teamScore = teamRoundStats.reduce((acc, curr) => 
          acc + parseScore(curr.GB), 0);
        
        const oppositionScore = oppositionRoundStats.reduce((acc, curr) => 
          acc + parseScore(curr.GB), 0);
  
        return {
          round,
          teamScore,
          oppositionScore,
          opponent: oppositionTeam
        };
      });
  
      const stats = {
        totalPlayers: [...new Set(teamPlayers.map(p => p.Player))].length,
        wins: matchResults.filter(m => m.teamScore > m.oppositionScore).length,
        losses: matchResults.filter(m => m.teamScore < m.oppositionScore).length,
        draws: matchResults.filter(m => m.teamScore === m.oppositionScore).length,
        scoresFor: (matchResults.reduce((acc, m) => acc + m.teamScore, 0) / rounds.length).toFixed(1),
        scoresAgainst: (matchResults.reduce((acc, m) => acc + m.oppositionScore, 0) / rounds.length).toFixed(1),
        avgFantasy: (teamPlayers.reduce((acc, curr) => acc + Number(curr.RP || 0), 0) / rounds.length).toFixed(1),
        avgDisposals: (teamPlayers.reduce((acc, curr) => 
          acc + (Number(curr.K || 0) + Number(curr.HB || 0)), 0) / rounds.length).toFixed(1),
        avgMarks: (teamPlayers.reduce((acc, curr) => acc + Number(curr.M || 0), 0) / rounds.length).toFixed(1),
        avgTackles: (teamPlayers.reduce((acc, curr) => acc + Number(curr.T || 0), 0) / rounds.length).toFixed(1),
        matchResults: matchResults,
        topPerformers: getTopPerformers(teamPlayers),
      };
      
      setTeamStats(stats);
    }
  }, [selectedTeam, playerData]);

  const calculateTeamAverage = (data, field) => {
    const sum = data.reduce((acc, curr) => acc + Number(curr[field] || 0), 0);
    return sum / data.length;
  };

  const getTopPerformers = (players) => {
    const uniquePlayers = [...new Set(players.map(p => p.Player))];
    const playerAverages = uniquePlayers.map(player => {
      const games = players.filter(p => p.Player === player);
      return {
        name: player,
        avgFantasy: Math.round(calculateTeamAverage(games, 'RP')),
        avgDisposals: (
          calculateTeamAverage(games, 'K') +
          calculateTeamAverage(games, 'HB')
        ).toFixed(1),
        games: games.length
      };
    });

    return playerAverages
      .sort((a, b) => Number(b.avgFantasy) - Number(a.avgFantasy))
      .slice(0, 5);
  };

  const askQuestion = async () => {
    setIsLoading(true);
    try {
      const apiUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:8000/api/ask/'
        : 'https://insight50.onrender.com/api/ask/';

      console.log('1. Starting API request');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          relevantData: {
            playerData: playerData.filter(p => p.Team === selectedTeam),
            teamStats,
            selectedTeam
          }
        })
      });

      const data = await response.json();
      console.log('2. API Response received:', data);
      console.log('3. Response has analysis?', Boolean(data.analysis));

      setResponse(data);
      console.log('4. Response state set to:', data);
    } catch (error) {
      console.error('Error in API call:', error);
    } finally {
      setIsLoading(false);
      console.log('5. Loading state finished');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-6">
      <div className="mb-12 relative">
        <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-blob"></div>
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-blob animation-delay-2000"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8">
        <div className="col-span-1 lg:col-span-3 space-y-4 lg:space-y-6">
          <div className="backdrop-blur-lg bg-[#1a1f2e]/80 p-6 rounded-2xl border border-gray-700/50 shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
              Select Team
            </h2>
            <select 
              className="w-full bg-gray-700/50 p-3 rounded-xl border border-gray-600/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300"
              onChange={(e) => setSelectedTeam(e.target.value)}
              value={selectedTeam}
            >
              <option value="">Select Team</option>
              {teams.map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>

          {teamStats && (
            <div className="backdrop-blur-lg bg-gray-800/50 p-6 rounded-2xl border border-gray-700/50 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                Team Profile
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Players Used</span>
                  <span className="text-lg font-bold text-emerald-400">{teamStats.totalPlayers}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Record</span>
                  <span className="text-lg font-bold text-emerald-400">
                    {teamStats.wins}W - {teamStats.losses}L
                    {teamStats.draws > 0 ? ` - ${teamStats.draws}D` : ''}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Avg Score For</span>
                  <span className="text-lg font-bold text-blue-400">{teamStats.scoresFor}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-xl">
                  <span className="text-gray-400">Avg Score Against</span>
                  <span className="text-lg font-bold text-red-400">{teamStats.scoresAgainst}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="col-span-1 lg:col-span-9 space-y-4 lg:space-y-8">
          {teamStats && (
            <>
              <div className="backdrop-blur-lg bg-gray-800/50 p-4 rounded-xl border border-gray-700/50 shadow-lg">
                <h2 className="text-sm font-semibold mb-3 text-emerald-400">
                  Team Overview
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <div className="text-gray-400 text-xs mb-1">Players Used</div>
                    <div className="text-lg font-bold text-emerald-400">{teamStats.totalPlayers}</div>
                  </div>
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <div className="text-gray-400 text-xs mb-1">Avg Fantasy</div>
                    <div className="text-lg font-bold text-blue-400">{teamStats.avgFantasy}</div>
                  </div>
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <div className="text-gray-400 text-xs mb-1">Avg Disposals</div>
                    <div className="text-lg font-bold text-purple-400">{teamStats.avgDisposals}</div>
                  </div>
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <div className="text-gray-400 text-xs mb-1">Avg Marks</div>
                    <div className="text-lg font-bold text-emerald-400">{teamStats.avgMarks}</div>
                  </div>
                </div>
              </div>

              {/* AI Section - Now stacked */}
              <div className="space-y-4">
                <div className="backdrop-blur-lg bg-gray-800/50 p-4 rounded-xl border border-gray-700/50 shadow-lg">
                  <h2 className="text-sm font-semibold mb-3 text-emerald-400">
                    Ask AI
                  </h2>
                  <div className="space-y-3">
                    <textarea
                      className="w-full bg-gray-700/50 p-3 rounded-lg border border-gray-600/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Ask about team performance..."
                      rows={3}
                    />
                    <button
                      onClick={askQuestion}
                      disabled={isLoading || !selectedTeam}
                      className="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 disabled:opacity-50"
                    >
                      {isLoading ? 'Analyzing...' : 'Ask Question'}
                    </button>
                  </div>
                </div>
                
                {/* AI Analysis */}
                <div className="backdrop-blur-lg bg-gray-800/50 p-6 rounded-2xl border border-gray-700/50 shadow-lg">
                    <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                      AI Analysis
                    </h2>
                    {response ? (
                      <div className="prose prose-invert max-w-none">
                        {response.analysis}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-center py-8">
                        {selectedTeam ? 'Ask a question to get AI insights about the team' : 'Select a team to begin analysis'}
                      </div>
                    )}
                </div>
              </div>

              <div className="backdrop-blur-lg bg-gray-800/50 p-8 rounded-2xl border border-gray-700/50 shadow-lg">
                <h2 className="text-xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-500">
                  Top Performers
                </h2>
                <div className="space-y-4">
                  {teamStats.topPerformers.map((player, index) => (
                    <div key={player.name} className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl">
                      <div>
                        <div className="font-semibold text-lg">{player.name}</div>
                        <div className="text-gray-400 text-sm">{player.games} games</div>
                      </div>
                      <div className="text-right">
                        <div className="text-emerald-400 font-bold">{player.avgFantasy} AF</div>
                        <div className="text-gray-400 text-sm">{player.avgDisposals} disposals</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamAnalysis;